/**
 * Der besondere Ring um das Zentrum.
 *
 * **Geometrie** (`docs/GAME_RULES.md`, Abschnitt 2, aus `docs/board-reference.svg`):
 * der Ring ist der vollständige Hex-Ring auf **Distanz 4** und besteht aus
 * **24 Feldern** — identisch mit Reihe E.
 *
 * Er zerfällt in sechs **gerade Kanten** und sechs **Eckfelder**. Jede Kante ist eine
 * gerade Hex-Linie aus fünf Feldern mit konstanter Cube-Komponente ±4 (drei
 * Kantenfelder plus die zwei geteilten Ecken, 6 × 5 − 6 = 24). Entlang einer Kante ist
 * die Ringbewegung deshalb **ohnehin identisch mit normaler Geradebewegung**; der
 * eigene Nachbarschaftsgraph wird nur an den sechs Ecken gebraucht, wo der Ring um 60°
 * abknickt. Das ist der geometrische Grund, warum die Regel „Ringbewegung wird als
 * Geradebewegung gezählt" widerspruchsfrei ist.
 *
 * **Richtungen** heißen hier `forward` und `backward` — bewusst neutral. Die Regel
 * erlaubt beide Umlaufrichtungen, die Zuordnung zu „im Uhrzeigersinn" ist eine Frage
 * der Darstellung und gehört damit in die UI, nicht in die Engine (die Engine kennt
 * keine Bildschirmkoordinaten).
 *
 * Die Ringdistanz ist bewusst eine Konstante und kein abgeleiteter Wert: sie gilt für
 * die Brettgröße der ersten Version (`sideLength` 9). Ob sie bei anderen Brettgrößen
 * mitskaliert oder frei konfigurierbar wird, ist eine **Regelfrage und noch nicht
 * gestellt** (`docs/RULE_CONFIG.md`). Bis dahin wird hier nichts geraten.
 */
import { isOnBoard } from './board.js';
import { hex, hexDistance, hexKey, hexRing, hexS } from './hex.js';
/** Hex-Distanz des Rings vom Zentrum, belegt durch die Referenzgrafik. */
export const RING_DISTANCE = 4;
/** Hat dieses Brett überhaupt einen Ring? Rein geometrisch, keine Spielregel. */
function hasRing(board) {
    return board.radius >= RING_DISTANCE;
}
/**
 * Die Ringfelder als geschlossener Rundgang: aufeinanderfolgende Einträge sind
 * Hex-Nachbarn, der letzte ist Nachbar des ersten.
 */
export function ringCells(board) {
    if (!hasRing(board))
        return [];
    return hexRing(RING_DISTANCE);
}
export function isRingCell(board, cell) {
    if (!hasRing(board))
        return false;
    return isOnBoard(board, cell) && hexDistance(hex(0, 0), cell) === RING_DISTANCE;
}
/** Position des Feldes im Rundgang, oder `undefined` außerhalb des Rings. */
export function ringIndex(board, cell) {
    if (!isRingCell(board, cell))
        return undefined;
    const key = hexKey(cell);
    const index = ringCells(board).findIndex((candidate) => hexKey(candidate) === key);
    return index === -1 ? undefined : index;
}
/**
 * Das nächste Ringfeld in der gewünschten Umlaufrichtung.
 *
 * `undefined`, wenn das Ausgangsfeld nicht auf dem Ring liegt — Ringbewegung ist nur
 * möglich, wenn die Figur ihren Zug bereits auf einem Ringfeld beginnt.
 */
export function ringNeighbor(board, cell, direction) {
    const index = ringIndex(board, cell);
    if (index === undefined)
        return undefined;
    const cells = ringCells(board);
    const step = direction === 'forward' ? 1 : cells.length - 1;
    return cells[(index + step) % cells.length];
}
/**
 * Eines der sechs Eckfelder des Rings — dort knickt der Ring ab, und nur dort weicht
 * ein Ringschritt von der geraden Hex-Achse ab.
 */
export function isRingCorner(board, cell) {
    if (!isRingCell(board, cell))
        return false;
    return cell.q === 0 || cell.r === 0 || hexS(cell) === 0;
}
//# sourceMappingURL=ring.js.map